// src/screens/AdminScreen.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { auth, db } from "../firebase";
import { getFeatureAccessState, mergeFeatureFlags } from "../utils/featureAccess";

import AdminSettings from "../components/admin/AdminSettings";
import InventoryFieldDefinitions from "../components/admin/InventoryFieldDefinitions";
import AccountsFieldDefinitions from "../components/admin/AccountsFieldDefinitions";
import AdminBillingTab from "../components/admin/AdminBillingTab";
import AdminConnectionsTab from "../components/admin/AdminConnectionsTab";
import AdminRecycleBinTab from "../components/admin/AdminRecycleBinTab";

import {
  Settings,
  CreditCard,
  Check,
  AlertCircle,
  Loader2,
  SlidersHorizontal,
  Cable,
  ArchiveRestore,
} from "lucide-react";

const AdminScreen = () => {
  const location = useLocation();
  // Core auth / profile / company
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [developerFeatureFlags, setDeveloperFeatureFlags] = useState({});
  const [companyId, setCompanyId] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("features");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  // Simple, real feature flags (wired to Firestore)
  const [requireNotificationSeen, setRequireNotificationSeen] = useState(false);
  const [notifyLowStock, setNotifyLowStock] = useState(false);

  // Customer catalog feature flag
  const [customerCatalogEnabled, setCustomerCatalogEnabled] = useState(false);

  // Sample order guardrails
  const [autoApproveUnder, setAutoApproveUnder] = useState(null);
  const [maxItemsPerOrder, setMaxItemsPerOrder] = useState(null);
  const [blockDuplicateRequestsEnabled, setBlockDuplicateRequestsEnabled] = useState(false);
  const [blockDuplicateDays, setBlockDuplicateDays] = useState(7);
  const [requireReasonForSample, setRequireReasonForSample] = useState(false);

  // Show inventory pricing to end users flag
  const [showInventoryPricingToEndUsers, setShowInventoryPricingToEndUsers] = useState(false);
  const [onlyOwnerCanSeeInventoryPricing, setOnlyOwnerCanSeeInventoryPricing] = useState(false);
  const [onlyOwnerCanDeactivateUsers, setOnlyOwnerCanDeactivateUsers] = useState(true);
  const [onlyOwnersCanAddAdmins, setOnlyOwnersCanAddAdmins] = useState(true);
  const [onlyOwnersCanApproveOrders, setOnlyOwnersCanApproveOrders] = useState(false);
  const [onlyOwnersCanSendOrdersToWarehouse, setOnlyOwnersCanSendOrdersToWarehouse] = useState(false);
  const [creatorsCanEditApprovedOrders, setCreatorsCanEditApprovedOrders] = useState(false);
  const [commercialTermsVisibility, setCommercialTermsVisibility] = useState("none");
  const [requireApprovalForAdmins, setRequireApprovalForAdmins] = useState(false);
  const [gridsVisibility, setGridsVisibility] = useState("all_admins");

  // ✅ Follow-up days + time of day (LIVE)
  // Rule:
  // - missing in Firestore => default 3
  // - explicitly null => disabled (blank)
  const [followUpEmailDays, setFollowUpEmailDays] = useState(3);
  const [followUpEmailTime, setFollowUpEmailTime] = useState("09:00");

  // ✅ LIVE: In-app notification rule toggles
  const [notifyTaskAssignedToUser, setNotifyTaskAssignedToUser] = useState(true);
  const [notifyTaskAssignedToGroup, setNotifyTaskAssignedToGroup] = useState(true);
  const [notifyTerritoryAssigned, setNotifyTerritoryAssigned] = useState(true);
  const [notifySampleStatusChanged, setNotifySampleStatusChanged] = useState(true);
  const [notifySampleAssignedToUser, setNotifySampleAssignedToUser] = useState(true);
  const [notifySampleStatusToCreator, setNotifySampleStatusToCreator] = useState(true);
  const [notifySampleStatusToAssignee, setNotifySampleStatusToAssignee] = useState(true);
  const [notifySampleStatusToAdmins, setNotifySampleStatusToAdmins] = useState(true);
  const [notifySampleComment, setNotifySampleComment] = useState(true);
  const [notifyOrderCreatedToAdmins, setNotifyOrderCreatedToAdmins] = useState(true);
  const [notifyOrderStatusToCreator, setNotifyOrderStatusToCreator] = useState(true);
  const [notifyOrderStatusToAssignee, setNotifyOrderStatusToAssignee] = useState(true);
  const [notifyOrderStatusToAdmins, setNotifyOrderStatusToAdmins] = useState(true);
  const [notifyOrderAssignmentToAssignee, setNotifyOrderAssignmentToAssignee] = useState(true);
  const [notifyOrderSentToWarehouse, setNotifyOrderSentToWarehouse] = useState(true);
  const [notifyOrderApproved, setNotifyOrderApproved] = useState(true);
  const [notifyOrderDelivered, setNotifyOrderDelivered] = useState(true);
  const [notifyOrderEmailCreator, setNotifyOrderEmailCreator] = useState(true);
  const [notifyOrderEmailAssignee, setNotifyOrderEmailAssignee] = useState(true);
  const [notifyOrderEmailAdmins, setNotifyOrderEmailAdmins] = useState(false);

  // ✅ NEW: Inventory/catalog notification toggles (defaults OFF)
  const [notifyNewSampleableItem, setNotifyNewSampleableItem] = useState(false);
  const [notifyMostPopularItemsThisWeek, setNotifyMostPopularItemsThisWeek] = useState(false);
  const [notifyFeaturedItem, setNotifyFeaturedItem] = useState(false);

  const [savingFeatures, setSavingFeatures] = useState(false);

  useEffect(() => {
    let unsubAuth;

    const init = async () => {
      setLoading(true);
      setError("");

      unsubAuth = auth.onAuthStateChanged(async (user) => {
        if (!user) {
          setFirebaseUser(null);
          setUserProfile(null);
          setCompany(null);
          setCompanyId(null);
          setTeamUsers([]);
          setLoading(false);
          return;
        }

        setFirebaseUser(user);

        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);

          if (!userSnap.exists()) {
            setUserProfile(null);
            setLoading(false);
            return;
          }

          const userData = userSnap.data();
          setUserProfile(userData);

          const compId = userData.companyId || user.uid;
          setCompanyId(compId);

          await loadCompany(compId);
          await loadTeamUsers(compId);
        } catch (err) {
          console.error("[AdminScreen] Error loading data:", err);
          setError("Failed to load admin settings. Please try again.");
        }

        setLoading(false);
      });
    };

    init();

    return () => {
      if (unsubAuth) unsubAuth();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadCompany = async (compId) => {
    if (!compId) return;

    try {
      const companyRef = doc(db, "companies", compId);
      const developerControlsRef = doc(db, "platformSettings", "developerControls");
      const [companySnap, developerControlsSnap] = await Promise.all([
        getDoc(companyRef),
        getDoc(developerControlsRef),
      ]);
      setDeveloperFeatureFlags(
        developerControlsSnap.exists() ? (developerControlsSnap.data()?.featureFlags || {}) : {}
      );

      if (companySnap.exists()) {
        const data = companySnap.data();
        setCompany(data);

        const flags = data.featureFlags || {};

        // Core flags
        setRequireNotificationSeen(!!flags.requireNotificationSeen);
        setNotifyLowStock(!!flags.notifyLowStock);

        // Catalog flag (support legacy top-level fallback)
        setCustomerCatalogEnabled(!!(flags.customerCatalogEnabled ?? data.customerCatalogEnabled));

        // Pricing visibility flag (support legacy top-level fallback)
        setShowInventoryPricingToEndUsers(
          !!(flags.showInventoryPricingToEndUsers ?? data.showInventoryPricingToEndUsers)
        );
        setOnlyOwnerCanSeeInventoryPricing(!!flags.onlyOwnerCanSeeInventoryPricing);
        setOnlyOwnerCanDeactivateUsers(flags.onlyOwnerCanDeactivateUsers !== false);
        setOnlyOwnersCanAddAdmins(flags.onlyOwnersCanAddAdmins !== false);
        setOnlyOwnersCanApproveOrders(!!flags.onlyOwnersCanApproveOrders);
        setOnlyOwnersCanSendOrdersToWarehouse(!!flags.onlyOwnersCanSendOrdersToWarehouse);
        setCreatorsCanEditApprovedOrders(!!flags.creatorsCanEditApprovedOrders);
        setCommercialTermsVisibility(
          ["none", "admin", "all"].includes(String(flags.commercialTermsVisibility || "").trim().toLowerCase())
            ? String(flags.commercialTermsVisibility).trim().toLowerCase()
            : "none"
        );
        setRequireApprovalForAdmins(!!flags.requireApprovalForAdmins);
        setGridsVisibility(
          ["owner_only", "all_admins", "all_users"].includes(String(flags.gridsVisibility || "").trim().toLowerCase())
            ? String(flags.gridsVisibility).trim().toLowerCase()
            : "all_admins"
        );

        // Sample order guardrails
        setAutoApproveUnder(typeof flags.autoApproveUnder === "number" ? flags.autoApproveUnder : null);
        setMaxItemsPerOrder(typeof flags.maxItemsPerOrder === "number" ? flags.maxItemsPerOrder : null);
        setBlockDuplicateRequestsEnabled(!!flags.blockDuplicateRequestsEnabled);
        setBlockDuplicateDays(
          typeof flags.blockDuplicateDays === "number" && flags.blockDuplicateDays > 0
            ? flags.blockDuplicateDays
            : 7
        );
        setRequireReasonForSample(!!flags.requireReasonForSample);

        // ✅ Follow-up settings
        // IMPORTANT RULE:
        // - If followUpEmailDays is MISSING => default 3 (ON)
        // - If followUpEmailDays exists but is null => disabled (blank)
        const hasFollowUpDays = Object.prototype.hasOwnProperty.call(flags, "followUpEmailDays");
        if (hasFollowUpDays) {
          setFollowUpEmailDays(
            typeof flags.followUpEmailDays === "number"
              ? Math.max(1, Math.floor(flags.followUpEmailDays))
              : null
          );
        } else {
          setFollowUpEmailDays(3);
        }

        setFollowUpEmailTime(typeof flags.followUpEmailTime === "string" ? flags.followUpEmailTime : "09:00");

        // In-app notification rules (default ON unless explicitly false)
        setNotifyTaskAssignedToUser(flags.inAppNotifyTaskAssignedToUser !== false);
        setNotifyTaskAssignedToGroup(flags.inAppNotifyTaskAssignedToGroup !== false);
        setNotifyTerritoryAssigned(flags.inAppNotifyTerritoryAssigned !== false);
        setNotifySampleStatusChanged(flags.inAppNotifySampleStatusChanged !== false);
        setNotifySampleAssignedToUser(flags.inAppNotifySampleAssignedToUser !== false);
        setNotifySampleStatusToCreator(flags.inAppNotifySampleStatusToCreator !== false);
        setNotifySampleStatusToAssignee(flags.inAppNotifySampleStatusToAssignee !== false);
        setNotifySampleStatusToAdmins(flags.inAppNotifySampleStatusToAdmins !== false);
        setNotifySampleComment(flags.inAppNotifySampleComment !== false);
        setNotifyOrderCreatedToAdmins(flags.inAppNotifyOrderCreatedToAdmins !== false);
        setNotifyOrderStatusToCreator(flags.inAppNotifyOrderStatusToCreator !== false);
        setNotifyOrderStatusToAssignee(flags.inAppNotifyOrderStatusToAssignee !== false);
        setNotifyOrderStatusToAdmins(flags.inAppNotifyOrderStatusToAdmins !== false);
        setNotifyOrderAssignmentToAssignee(flags.inAppNotifyOrderAssignmentToAssignee !== false);
        setNotifyOrderSentToWarehouse(flags.notifyOnOrderSentToWarehouse !== false);
        setNotifyOrderApproved(flags.notifyOnOrderApproved !== false);
        setNotifyOrderDelivered(flags.notifyOnOrderDelivered !== false);
        setNotifyOrderEmailCreator(flags.notifyOrderEmailCreator !== false);
        setNotifyOrderEmailAssignee(flags.notifyOrderEmailAssignee !== false);
        setNotifyOrderEmailAdmins(!!flags.notifyOrderEmailAdmins);

        // Inventory/catalog notification rules (default OFF unless explicitly true)
        setNotifyNewSampleableItem(!!flags.notifyNewSampleableItem);
        setNotifyMostPopularItemsThisWeek(!!flags.notifyMostPopularItemsThisWeek);
        setNotifyFeaturedItem(!!flags.notifyFeaturedItem);
      } else {
        setCompany(null);

        // If company doc doesn't exist yet, default ON at 3
        setFollowUpEmailDays(3);
        setFollowUpEmailTime("09:00");
      }
    } catch (err) {
      console.error("[AdminScreen] Error loading company:", err);
    }
  };

  const loadTeamUsers = async (compId) => {
    try {
      const usersCol = collection(db, "users");
      const qUsers = query(usersCol, where("companyId", "==", compId));
      const snap = await getDocs(qUsers);

      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return aTime - bTime;
      });

      setTeamUsers(list);
    } catch (err) {
      console.error("[AdminScreen] Error loading team users:", err);
      setTeamUsers([]);
    }
  };

  // Role flags
  const role = userProfile?.role || "";
  const normalizedRole = role.toLowerCase();
  const isOwner = normalizedRole === "owner";
  const isAdminOrOwner = normalizedRole === "admin" || isOwner;
  const mergedFeatureFlags = useMemo(
    () => mergeFeatureFlags(company?.featureFlags || {}, developerFeatureFlags || {}),
    [company?.featureFlags, developerFeatureFlags]
  );
  const featureAccessBase = useMemo(
    () => ({
      featureFlags: mergedFeatureFlags,
      userEmail: firebaseUser?.email || userProfile?.email || "",
      planType: company?.planType || userProfile?.planType || null,
      subscriptionActive:
        typeof company?.subscriptionActive === "boolean"
          ? company.subscriptionActive
          : userProfile?.subscriptionActive === true,
      stripeSubscriptionId: company?.stripeSubscriptionId || "",
      seatLimits: company?.seatLimits || null,
    }),
    [
      mergedFeatureFlags,
      firebaseUser?.email,
      userProfile?.email,
      company?.planType,
      userProfile?.planType,
      company?.subscriptionActive,
      userProfile?.subscriptionActive,
      company?.stripeSubscriptionId,
      company?.seatLimits,
    ]
  );
  const adminSettingsFeatureAccess = useMemo(
    () => getFeatureAccessState({ featureKey: "adminSettings", ...featureAccessBase }),
    [featureAccessBase]
  );
  const connectionsFeatureAccess = useMemo(
    () => getFeatureAccessState({ featureKey: "connections", ...featureAccessBase }),
    [featureAccessBase]
  );
  const adminSettingsHiddenEntirely = adminSettingsFeatureAccess.reason === "hidden_entirely";
  const connectionsHiddenEntirely = connectionsFeatureAccess.reason === "hidden_entirely";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get("tab");
    if (!requestedTab) return;

    const allowedTabs = isOwner
      ? new Set([
          ...(adminSettingsFeatureAccess.allowed ? ["features"] : []),
          "billing",
          ...(isMobile ? [] : ["inventoryFields", "accountFields"]),
          ...(isMobile ? [] : connectionsFeatureAccess.allowed ? ["connections"] : []),
        ])
      : new Set([
          ...(adminSettingsFeatureAccess.allowed ? ["features"] : []),
          ...(isMobile ? [] : ["inventoryFields", "accountFields"]),
          ...(isMobile ? [] : connectionsFeatureAccess.allowed ? ["connections"] : []),
        ]);

    if (allowedTabs.has(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [adminSettingsFeatureAccess.allowed, connectionsFeatureAccess.allowed, isOwner, isMobile, location.search]);

  useEffect(() => {
    const fallbackTab = isOwner ? "billing" : "features";
    if (!isOwner && activeTab === "billing") {
      setActiveTab(adminSettingsFeatureAccess.allowed ? "features" : fallbackTab);
      return;
    }
    if (isMobile && ["inventoryFields", "accountFields", "connections"].includes(activeTab)) {
      setActiveTab(adminSettingsFeatureAccess.allowed ? "features" : fallbackTab);
      return;
    }
    if (!adminSettingsFeatureAccess.allowed && activeTab === "features") {
      setActiveTab(fallbackTab);
      return;
    }
    if (!connectionsFeatureAccess.allowed && activeTab === "connections") {
      setActiveTab(adminSettingsFeatureAccess.allowed ? "features" : fallbackTab);
    }
  }, [adminSettingsFeatureAccess.allowed, connectionsFeatureAccess.allowed, isOwner, isMobile, activeTab]);

  const handleSaveBasicFlags = async () => {
    if (!companyId) return;
    setSavingFeatures(true);
    setError("");
    setSaveSuccess(false);

    const companyRef = doc(db, "companies", companyId);

    // normalize time (store null if empty)
    const timeToSave = (followUpEmailTime || "").toString().trim();
    const normalizedTime = timeToSave === "" ? null : timeToSave;

    // followUpEmailDays:
    // - number => save number (minimum 1 full day)
    // - null => save null (disabled)
    const daysToSave =
      typeof followUpEmailDays === "number" ? Math.max(1, Math.floor(followUpEmailDays)) : null;

    try {
      try {
        await updateDoc(companyRef, {
          "featureFlags.requireNotificationSeen": !!requireNotificationSeen,
          "featureFlags.notifyLowStock": !!notifyLowStock,
          "featureFlags.customerCatalogEnabled": !!customerCatalogEnabled,
          "featureFlags.showInventoryPricingToEndUsers": !!showInventoryPricingToEndUsers,
          "featureFlags.onlyOwnerCanSeeInventoryPricing": !!onlyOwnerCanSeeInventoryPricing,
          "featureFlags.onlyOwnerCanDeactivateUsers": !!onlyOwnerCanDeactivateUsers,
          "featureFlags.onlyOwnersCanAddAdmins": !!onlyOwnersCanAddAdmins,
          "featureFlags.onlyOwnersCanApproveOrders": !!onlyOwnersCanApproveOrders,
          "featureFlags.onlyOwnersCanSendOrdersToWarehouse": !!onlyOwnersCanSendOrdersToWarehouse,
          "featureFlags.creatorsCanEditApprovedOrders": !!creatorsCanEditApprovedOrders,
          "featureFlags.commercialTermsVisibility": commercialTermsVisibility || "none",
          "featureFlags.requireApprovalForAdmins": !!requireApprovalForAdmins,
          "featureFlags.gridsVisibility": gridsVisibility || "all_admins",

          "featureFlags.autoApproveUnder": autoApproveUnder,
          "featureFlags.maxItemsPerOrder": maxItemsPerOrder,
          "featureFlags.blockDuplicateRequestsEnabled": !!blockDuplicateRequestsEnabled,
          "featureFlags.blockDuplicateDays":
            typeof blockDuplicateDays === "number" && blockDuplicateDays > 0 ? blockDuplicateDays : 7,
          "featureFlags.requireReasonForSample": !!requireReasonForSample,

          // ✅ Follow-up settings (days + time)
          "featureFlags.followUpEmailDays": daysToSave,
          "featureFlags.followUpEmailTime": normalizedTime,

          "featureFlags.inAppNotifyTaskAssignedToUser": notifyTaskAssignedToUser !== false,
          "featureFlags.inAppNotifyTaskAssignedToGroup": notifyTaskAssignedToGroup !== false,
          "featureFlags.inAppNotifyTerritoryAssigned": notifyTerritoryAssigned !== false,
          "featureFlags.inAppNotifySampleStatusChanged": notifySampleStatusChanged !== false,
          "featureFlags.inAppNotifySampleAssignedToUser": notifySampleAssignedToUser !== false,
          "featureFlags.inAppNotifySampleStatusToCreator": notifySampleStatusToCreator !== false,
          "featureFlags.inAppNotifySampleStatusToAssignee": notifySampleStatusToAssignee !== false,
          "featureFlags.inAppNotifySampleStatusToAdmins": notifySampleStatusToAdmins !== false,
          "featureFlags.inAppNotifySampleComment": notifySampleComment !== false,
          "featureFlags.inAppNotifyOrderCreatedToAdmins": notifyOrderCreatedToAdmins !== false,
          "featureFlags.inAppNotifyOrderStatusToCreator": notifyOrderStatusToCreator !== false,
          "featureFlags.inAppNotifyOrderStatusToAssignee": notifyOrderStatusToAssignee !== false,
          "featureFlags.inAppNotifyOrderStatusToAdmins": notifyOrderStatusToAdmins !== false,
          "featureFlags.inAppNotifyOrderAssignmentToAssignee": notifyOrderAssignmentToAssignee !== false,
          "featureFlags.notifyOnOrderSentToWarehouse": notifyOrderSentToWarehouse !== false,
          "featureFlags.notifyOnOrderApproved": notifyOrderApproved !== false,
          "featureFlags.notifyOnOrderDelivered": notifyOrderDelivered !== false,
          "featureFlags.notifyOrderEmailCreator": notifyOrderEmailCreator !== false,
          "featureFlags.notifyOrderEmailAssignee": notifyOrderEmailAssignee !== false,
          "featureFlags.notifyOrderEmailAdmins": !!notifyOrderEmailAdmins,

          "featureFlags.notifyNewSampleableItem": !!notifyNewSampleableItem,
          "featureFlags.notifyMostPopularItemsThisWeek": !!notifyMostPopularItemsThisWeek,
          "featureFlags.notifyFeaturedItem": !!notifyFeaturedItem,

          updatedAt: serverTimestamp(),
        });
      } catch (e) {
        await setDoc(
          companyRef,
          {
            featureFlags: {
              requireNotificationSeen: !!requireNotificationSeen,
              notifyLowStock: !!notifyLowStock,
              customerCatalogEnabled: !!customerCatalogEnabled,
              showInventoryPricingToEndUsers: !!showInventoryPricingToEndUsers,
              onlyOwnerCanSeeInventoryPricing: !!onlyOwnerCanSeeInventoryPricing,
              onlyOwnerCanDeactivateUsers: !!onlyOwnerCanDeactivateUsers,
              onlyOwnersCanAddAdmins: !!onlyOwnersCanAddAdmins,
              onlyOwnersCanApproveOrders: !!onlyOwnersCanApproveOrders,
              onlyOwnersCanSendOrdersToWarehouse: !!onlyOwnersCanSendOrdersToWarehouse,
              creatorsCanEditApprovedOrders: !!creatorsCanEditApprovedOrders,
              commercialTermsVisibility: commercialTermsVisibility || "none",
              requireApprovalForAdmins: !!requireApprovalForAdmins,
              gridsVisibility: gridsVisibility || "all_admins",

              autoApproveUnder,
              maxItemsPerOrder,
              blockDuplicateRequestsEnabled: !!blockDuplicateRequestsEnabled,
              blockDuplicateDays:
                typeof blockDuplicateDays === "number" && blockDuplicateDays > 0 ? blockDuplicateDays : 7,
              requireReasonForSample: !!requireReasonForSample,

              // ✅ Follow-up settings
              followUpEmailDays: daysToSave,
              followUpEmailTime: normalizedTime,

              inAppNotifyTaskAssignedToUser: notifyTaskAssignedToUser !== false,
              inAppNotifyTaskAssignedToGroup: notifyTaskAssignedToGroup !== false,
              inAppNotifyTerritoryAssigned: notifyTerritoryAssigned !== false,
              inAppNotifySampleStatusChanged: notifySampleStatusChanged !== false,
              inAppNotifySampleAssignedToUser: notifySampleAssignedToUser !== false,
              inAppNotifySampleStatusToCreator: notifySampleStatusToCreator !== false,
              inAppNotifySampleStatusToAssignee: notifySampleStatusToAssignee !== false,
              inAppNotifySampleStatusToAdmins: notifySampleStatusToAdmins !== false,
              inAppNotifySampleComment: notifySampleComment !== false,
              inAppNotifyOrderCreatedToAdmins: notifyOrderCreatedToAdmins !== false,
              inAppNotifyOrderStatusToCreator: notifyOrderStatusToCreator !== false,
              inAppNotifyOrderStatusToAssignee: notifyOrderStatusToAssignee !== false,
              inAppNotifyOrderStatusToAdmins: notifyOrderStatusToAdmins !== false,
              inAppNotifyOrderAssignmentToAssignee: notifyOrderAssignmentToAssignee !== false,
              notifyOnOrderSentToWarehouse: notifyOrderSentToWarehouse !== false,
              notifyOnOrderApproved: notifyOrderApproved !== false,
              notifyOnOrderDelivered: notifyOrderDelivered !== false,
              notifyOrderEmailCreator: notifyOrderEmailCreator !== false,
              notifyOrderEmailAssignee: notifyOrderEmailAssignee !== false,
              notifyOrderEmailAdmins: !!notifyOrderEmailAdmins,

              notifyNewSampleableItem: !!notifyNewSampleableItem,
              notifyMostPopularItemsThisWeek: !!notifyMostPopularItemsThisWeek,
              notifyFeaturedItem: !!notifyFeaturedItem,
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);

      await loadCompany(companyId);
    } catch (err) {
      console.error("[AdminScreen] Error saving flags:", err);
      setError("Could not save feature flags. Please try again.");
    } finally {
      setSavingFeatures(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 text-lg font-medium">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (!firebaseUser || !isAdminOrOwner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center border border-red-100">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600">
            You don't have permission to view this page. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4 -mx-4 md:-mx-6 lg:-mx-8 -mt-4 md:-mt-6 lg:-mt-8">
      <div className="w-full max-w-none mx-0 px-0 py-1">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-6">
          <div className="border-b border-slate-200 bg-slate-50/80">
            <nav className="flex flex-wrap items-stretch">
              {adminSettingsHiddenEntirely ? null : adminSettingsFeatureAccess.allowed ? (
                <button
                  type="button"
                  onClick={() => setActiveTab("features")}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
                    activeTab === "features"
                      ? "text-blue-700 bg-white shadow-[inset_4px_0_0_0_rgb(37,99,235)]"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white/70"
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  <span>Settings</span>
                  
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setActiveTab(isOwner ? "billing" : "inventoryFields")}
                  className="flex items-center gap-2 px-6 py-4 text-sm font-medium text-slate-400 bg-slate-50 border-r border-slate-100 cursor-pointer relative"
                  title={adminSettingsFeatureAccess.lockMessage || (adminSettingsFeatureAccess.reason === "paid_only" ? "Paid plan required" : "Hidden by developer")}
                >
                  <Settings className="w-5 h-5" />
                  <span>Settings</span>
                  <span className="inline-flex items-center justify-center rounded-full border border-slate-300 p-1">
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-5a2 2 0 00-2-2h-1V9a5 5 0 00-10 0v3H6a2 2 0 00-2 2v5a2 2 0 002 2zm3-9V9a3 3 0 016 0v3H9z" />
                    </svg>
                  </span>
                </button>
              )}

              {isOwner && (
                <button
                  type="button"
                  onClick={() => setActiveTab("billing")}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
                    activeTab === "billing"
                      ? "text-blue-700 bg-white shadow-[inset_4px_0_0_0_rgb(37,99,235)]"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white/70"
                  }`}
                >
                  <CreditCard className="w-5 h-5" />
                  <span>Billing</span>
                  
                </button>
              )}

              {!isMobile && (
                <>
                  <button
                    type="button"
                    onClick={() => setActiveTab("inventoryFields")}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
                      activeTab === "inventoryFields"
                        ? "text-blue-700 bg-white shadow-[inset_4px_0_0_0_rgb(37,99,235)]"
                        : "text-slate-600 hover:text-slate-900 hover:bg-white/70"
                    }`}
                  >
                    <SlidersHorizontal className="w-5 h-5" />
                    <span>Inventory Fields</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab("accountFields")}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
                      activeTab === "accountFields"
                        ? "text-blue-700 bg-white shadow-[inset_4px_0_0_0_rgb(37,99,235)]"
                        : "text-slate-600 hover:text-slate-900 hover:bg-white/70"
                    }`}
                  >
                    <SlidersHorizontal className="w-5 h-5" />
                    <span>Account Fields</span>
                  </button>
                </>
              )}

              {isAdminOrOwner && (
                <button
                  type="button"
                  onClick={() => setActiveTab("recycleBin")}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
                    activeTab === "recycleBin"
                      ? "text-blue-700 bg-white shadow-[inset_4px_0_0_0_rgb(37,99,235)]"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white/70"
                  }`}
                >
                  <ArchiveRestore className="w-5 h-5" />
                  <span>Recycle Bin</span>
                </button>
              )}

              {!isMobile && isAdminOrOwner && !connectionsHiddenEntirely && (
                connectionsFeatureAccess.allowed ? (
                  <button
                    type="button"
                    onClick={() => setActiveTab("connections")}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
                      activeTab === "connections"
                        ? "text-blue-700 bg-white shadow-[inset_4px_0_0_0_rgb(37,99,235)]"
                        : "text-slate-600 hover:text-slate-900 hover:bg-white/70"
                    }`}
                  >
                    <Cable className="w-5 h-5" />
                    <span>Connections</span>
                    
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setActiveTab("features")}
                    className="flex items-center gap-2 px-6 py-4 text-sm font-medium text-slate-400 bg-slate-50 border-l border-slate-100 cursor-pointer relative"
                    title={connectionsFeatureAccess.lockMessage || (connectionsFeatureAccess.reason === "paid_only" ? "Paid plan required" : "Hidden by developer")}
                  >
                    <Cable className="w-5 h-5" />
                    <span>Connections</span>
                    <span className="inline-flex items-center justify-center rounded-full border border-slate-300 p-1">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-5a2 2 0 00-2-2h-1V9a5 5 0 00-10 0v3H6a2 2 0 00-2 2v5a2 2 0 002 2zm3-9V9a3 3 0 016 0v3H9z" />
                      </svg>
                    </span>
                  </button>
                )
              )}
            </nav>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 flex-1">{error}</p>
              </div>
            )}

            {saveSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800 flex-1">Settings saved successfully!</p>
              </div>
            )}

            {activeTab === "features" && adminSettingsFeatureAccess.allowed && (
              <AdminSettings
                requireNotificationSeen={requireNotificationSeen}
                setRequireNotificationSeen={setRequireNotificationSeen}
                notifyLowStock={notifyLowStock}
                setNotifyLowStock={setNotifyLowStock}
                customerCatalogEnabled={customerCatalogEnabled}
              setCustomerCatalogEnabled={setCustomerCatalogEnabled}
              showInventoryPricingToEndUsers={showInventoryPricingToEndUsers}
              setShowInventoryPricingToEndUsers={setShowInventoryPricingToEndUsers}
              onlyOwnerCanSeeInventoryPricing={onlyOwnerCanSeeInventoryPricing}
              setOnlyOwnerCanSeeInventoryPricing={setOnlyOwnerCanSeeInventoryPricing}
              onlyOwnerCanDeactivateUsers={onlyOwnerCanDeactivateUsers}
              setOnlyOwnerCanDeactivateUsers={setOnlyOwnerCanDeactivateUsers}
              onlyOwnersCanAddAdmins={onlyOwnersCanAddAdmins}
              setOnlyOwnersCanAddAdmins={setOnlyOwnersCanAddAdmins}
              onlyOwnersCanApproveOrders={onlyOwnersCanApproveOrders}
              setOnlyOwnersCanApproveOrders={setOnlyOwnersCanApproveOrders}
              onlyOwnersCanSendOrdersToWarehouse={onlyOwnersCanSendOrdersToWarehouse}
              setOnlyOwnersCanSendOrdersToWarehouse={setOnlyOwnersCanSendOrdersToWarehouse}
              creatorsCanEditApprovedOrders={creatorsCanEditApprovedOrders}
              setCreatorsCanEditApprovedOrders={setCreatorsCanEditApprovedOrders}
              commercialTermsVisibility={commercialTermsVisibility}
              setCommercialTermsVisibility={setCommercialTermsVisibility}
              requireApprovalForAdmins={requireApprovalForAdmins}
              setRequireApprovalForAdmins={setRequireApprovalForAdmins}
              gridsVisibility={gridsVisibility}
              setGridsVisibility={setGridsVisibility}
                autoApproveUnder={autoApproveUnder}
                setAutoApproveUnder={setAutoApproveUnder}
                maxItemsPerOrder={maxItemsPerOrder}
                setMaxItemsPerOrder={setMaxItemsPerOrder}
                blockDuplicateRequestsEnabled={blockDuplicateRequestsEnabled}
                setBlockDuplicateRequestsEnabled={setBlockDuplicateRequestsEnabled}
                blockDuplicateDays={blockDuplicateDays}
                setBlockDuplicateDays={setBlockDuplicateDays}
                requireReasonForSample={requireReasonForSample}
                setRequireReasonForSample={setRequireReasonForSample}
                followUpDaysAfterCompleted={followUpEmailDays}
                setFollowUpDaysAfterCompleted={setFollowUpEmailDays}
                followUpTimeOfDay={followUpEmailTime}
                setFollowUpTimeOfDay={setFollowUpEmailTime}
                notifyTaskAssignedToUser={notifyTaskAssignedToUser}
                setNotifyTaskAssignedToUser={setNotifyTaskAssignedToUser}
                notifyTaskAssignedToGroup={notifyTaskAssignedToGroup}
                setNotifyTaskAssignedToGroup={setNotifyTaskAssignedToGroup}
                notifyTerritoryAssigned={notifyTerritoryAssigned}
                setNotifyTerritoryAssigned={setNotifyTerritoryAssigned}
                notifySampleStatusChanged={notifySampleStatusChanged}
                setNotifySampleStatusChanged={setNotifySampleStatusChanged}
                notifySampleAssignedToUser={notifySampleAssignedToUser}
                setNotifySampleAssignedToUser={setNotifySampleAssignedToUser}
                notifySampleStatusToCreator={notifySampleStatusToCreator}
                setNotifySampleStatusToCreator={setNotifySampleStatusToCreator}
                notifySampleStatusToAssignee={notifySampleStatusToAssignee}
                setNotifySampleStatusToAssignee={setNotifySampleStatusToAssignee}
                notifySampleStatusToAdmins={notifySampleStatusToAdmins}
                setNotifySampleStatusToAdmins={setNotifySampleStatusToAdmins}
                notifySampleComment={notifySampleComment}
                setNotifySampleComment={setNotifySampleComment}
                notifyOrderCreatedToAdmins={notifyOrderCreatedToAdmins}
                setNotifyOrderCreatedToAdmins={setNotifyOrderCreatedToAdmins}
                notifyOrderStatusToCreator={notifyOrderStatusToCreator}
                setNotifyOrderStatusToCreator={setNotifyOrderStatusToCreator}
                notifyOrderStatusToAssignee={notifyOrderStatusToAssignee}
                setNotifyOrderStatusToAssignee={setNotifyOrderStatusToAssignee}
                notifyOrderStatusToAdmins={notifyOrderStatusToAdmins}
                setNotifyOrderStatusToAdmins={setNotifyOrderStatusToAdmins}
                notifyOrderAssignmentToAssignee={notifyOrderAssignmentToAssignee}
                setNotifyOrderAssignmentToAssignee={setNotifyOrderAssignmentToAssignee}
                notifyOrderSentToWarehouse={notifyOrderSentToWarehouse}
                setNotifyOrderSentToWarehouse={setNotifyOrderSentToWarehouse}
                notifyOrderApproved={notifyOrderApproved}
                setNotifyOrderApproved={setNotifyOrderApproved}
                notifyOrderDelivered={notifyOrderDelivered}
                setNotifyOrderDelivered={setNotifyOrderDelivered}
                notifyOrderEmailCreator={notifyOrderEmailCreator}
                setNotifyOrderEmailCreator={setNotifyOrderEmailCreator}
                notifyOrderEmailAssignee={notifyOrderEmailAssignee}
                setNotifyOrderEmailAssignee={setNotifyOrderEmailAssignee}
                notifyOrderEmailAdmins={notifyOrderEmailAdmins}
                setNotifyOrderEmailAdmins={setNotifyOrderEmailAdmins}
                notifyNewSampleableItem={notifyNewSampleableItem}
                setNotifyNewSampleableItem={setNotifyNewSampleableItem}
                notifyMostPopularItemsThisWeek={notifyMostPopularItemsThisWeek}
                setNotifyMostPopularItemsThisWeek={setNotifyMostPopularItemsThisWeek}
                notifyFeaturedItem={notifyFeaturedItem}
                setNotifyFeaturedItem={setNotifyFeaturedItem}
                savingFeatures={savingFeatures}
                onSaveBasicFlags={handleSaveBasicFlags}
              />
            )}

            {activeTab === "inventoryFields" && <InventoryFieldDefinitions companyId={companyId} />}

            {activeTab === "accountFields" && <AccountsFieldDefinitions companyId={companyId} />}

            {activeTab === "recycleBin" && isAdminOrOwner && (
              <AdminRecycleBinTab companyId={companyId} />
            )}

            {activeTab === "billing" && isOwner && (
              <AdminBillingTab
                firebaseUser={firebaseUser}
                userProfile={userProfile}
                company={company}
                companyId={companyId}
                teamUsers={teamUsers}
                isOwner={isOwner}
                setError={setError}
                loadCompany={loadCompany}
              />
            )}

            {activeTab === "connections" && isAdminOrOwner && connectionsFeatureAccess.allowed && (
              <AdminConnectionsTab
                companyId={companyId}
                company={company}
                isAdminOrOwner={isAdminOrOwner}
                isOwner={isOwner}
                onRefreshCompany={loadCompany}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminScreen;
